import { useCallback } from "react";

import { STATUS_LABEL_KEYS } from "@/modules/dashboard/payments-activity/constants/paymentsActivity.constants";
import useTranslations from "@/shared/lang/useTranslations";

import type { RuntimeWalletTransaction } from "../types/payments-wallets.types";

/**
 * Exact-match map on the free-text `description` the runtime writes, same source strings the ledger explorer
 * resolves (`LEDGER_ENTRY_LABEL_KEYS`) — but worded for a WALLET statement, not a journal. A client reading
 * their movements is not helped by "Causación y reserva de fondos", nor by the industry word for it: they
 * need "Dinero enviado".
 */
const WALLET_ENTRY_LABEL_KEYS: Record<string, string> = {
  "payin detected": "entry_payin_detected_wallet",
  "payin confirmed": "entry_payin_confirmed_wallet",
  "payout reserve": "entry_payout_reserve_wallet",
  swap: "entry_swap_wallet",
  "coloca top up": "entry_coloca_top_up_wallet",
  "coloca refund": "entry_coloca_refund_wallet",
};

/** The runtime interpolates the reason into the release description, so it is matched by prefix. */
const WALLET_ENTRY_LABEL_PREFIXES: readonly { prefix: string; key: string }[] = [
  { prefix: "payout release", key: "entry_payout_release_wallet" },
];

export interface WalletEntryLabels {
  /** Translated description, or the raw English text when the runtime writes something unmapped. */
  describe: (transaction: RuntimeWalletTransaction) => string | null;
  /** Translated status of the owning operation, or null when there is none. */
  statusOf: (transaction: RuntimeWalletTransaction) => string | null;
}

export function useWalletEntryLabels(): WalletEntryLabels {
  const { t } = useTranslations("wallets");
  const { t: tActivity } = useTranslations("paymentsActivity");

  const describe = useCallback(
    (transaction: RuntimeWalletTransaction): string | null => {
      const card = transaction.operation_card_top_up;
      if (card) {
        const key =
          transaction.operation_type === "REVERSE" ? "entry_card_top_up_reversal" : "entry_card_top_up";
        return t(key, { last4: card.last_4 ?? "" });
      }
      const description = transaction.description?.trim();
      if (!description) return null;

      const exact = WALLET_ENTRY_LABEL_KEYS[description];
      if (exact) return t(exact as never);

      const prefixed = WALLET_ENTRY_LABEL_PREFIXES.find((rule) => description.startsWith(rule.prefix));
      if (prefixed) return t(prefixed.key as never);

      // Unmapped descriptions render verbatim rather than being forced into a wrong label — hiding a
      // movement's own words would hide money.
      return description;
    },
    [t],
  );

  const statusOf = useCallback(
    (transaction: RuntimeWalletTransaction): string | null => {
      const status = transaction.operation_status;
      if (!status) return null;
      // Payin and payout share this map by raw status name; an unknown new status falls back to itself.
      const key = STATUS_LABEL_KEYS[status];
      return key ? tActivity(key) : status;
    },
    [tActivity],
  );

  return { describe, statusOf };
}
